/**
 * createPostContext — shared state for the Create Post flow and sub-screens.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
} from 'react';
import { runAfterInteractions } from '../platform/scheduler';
import { useTranslation } from '../i18n/useTranslation';
import { useProfile } from './ProfileContext';
import { useFeed } from './FeedContext';
import { ToastService } from '../platform/notifier';
import { useGroup } from './GroupContext';
import feedService, {
  BuddiesTagsUnresolvedError,
  isPersistedPostId,
  mapVisibilityIntToPrivacy,
} from '../api/services/feedService';
import { isHeldForReview } from '../api/moderation/moderationStatus';
import { runAfterKeyboardDismiss } from '../utils/runAfterKeyboardDismiss';
import {
  buddyKey,
  collapseHashtagPrefixes,
  normalizeHashtagLabel,
  normalizeHashtagList,
  splitSeparateHashtags,
} from '../utils/postDisplay';
import { resolveLayoutForAlbum } from '../utils/postImageLayout';
import {
  resolvePublishedPostMedia,
  resolveSubmittedPostMedia,
} from '../utils/publishedPostMedia';
import { convertPostContentToPlainText } from '../utils/postContent';
import {
  rebuildEditedReshareContent,
  splitEditableReshareContent,
} from '../utils/reshareContent';
import {
  analytics,
  ANALYTICS_EVENTS,
} from '../analytics';
import {
  POST_PRIVACY,
  POST_MEDIA_TYPE,
  POST_IMAGE_LAYOUT,
  STORAGE_KEYS,
  POST_GALLERY_MAX_IMAGES,
} from '../constants';
import type {
  CreatePostContextValue,
  CreatePostSheet,
  CreatePostSubmitResult,
  FeedPost,
  MediaFile,
  MediaPickResponse,
  PostImageLayout,
  PostPrivacy,
  TaggedBuddy,
} from '../types';

const createPostContext = createContext<CreatePostContextValue | null>(null);

const FAILED_SUBMIT: CreatePostSubmitResult = {
  success: false,
  heldForReview: false,
};

const taggedBuddyListsEqual = (a: TaggedBuddy[], b: TaggedBuddy[]): boolean => {
  const keysA = a.map(buddyKey).sort();
  const keysB = b.map(buddyKey).sort();
  return keysA.length === keysB.length && keysA.every((key, index) => key === keysB[index]);
};

interface FormState {
  content: string;
  privacy: PostPrivacy;
  selectedHashtags: string[];
  selectedFeeling: string | null;
  taggedBuddies: TaggedBuddy[];
  media: MediaFile | null;
  mediaFiles: MediaFile[];
  imageLayout: PostImageLayout | null;
}

const resetFormState = (): FormState => ({
  content: '',
  privacy: POST_PRIVACY.PUBLIC as PostPrivacy,
  selectedHashtags: [],
  selectedFeeling: null,
  taggedBuddies: [],
  media: null,
  mediaFiles: [],
  imageLayout: null,
});

const toImageMediaFile = (asset: MediaFile): MediaFile => ({
  ...asset,
  mediaType: POST_MEDIA_TYPE.IMAGE,
});

export const CreatePostProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const { t } = useTranslation();
  const { profile, fetchProfile } = useProfile();
  const { updatePost, syncNewPosts, refreshFeed } = useFeed();
  const { prependGroupPosts, updateGroupPost, fetchGroupPosts } = useGroup();

  const [content, setContent] = useState('');
  const [privacy, setPrivacy] = useState<PostPrivacy>(
    POST_PRIVACY.PUBLIC as PostPrivacy,
  );
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
  const [selectedFeeling, setSelectedFeeling] = useState<string | null>(null);
  const [taggedBuddies, setTaggedBuddies] = useState<TaggedBuddy[]>([]);
  const [media, setMedia] = useState<MediaFile | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [imageLayout, setImageLayout] = useState<PostImageLayout | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState<CreatePostSheet>(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPostId, setCreatedPostId] = useState<string | null>(null);
  /**
   * Set when the last successful save came back held for review. The save still
   * succeeded, so this is not an error — the caller uses it to swap the success
   * copy for the "visible only to you" explanation.
   */
  const [submittedHeldForReview, setSubmittedHeldForReview] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingPost, _setEditingPost] = useState<FeedPost | null>(null);
  const [protectedReshareContent, setProtectedReshareContent] =
    useState<string | null>(null);
  const isEditMode = !!editingPostId;
  /** Tagged buddies when edit mode opened — used to omit BuddiesTags when unchanged. */
  const originalTaggedBuddiesRef = useRef<TaggedBuddy[]>([]);

  useEffect(() => {
    // Defer non-critical fetches until after the create-post transition settles.
    const task = runAfterInteractions(() => {
      if (!profile) {
        fetchProfile().catch(() => undefined);
      }
    });
    return () => task.cancel();
  }, [profile, fetchProfile]);

  const hasUnsavedChanges = useMemo(
    () =>
      content.trim().length > 0 ||
      media !== null ||
      mediaFiles.length > 0 ||
      imageLayout !== null ||
      selectedHashtags.length > 0 ||
      selectedFeeling !== null ||
      taggedBuddies.length > 0,
    [
      content,
      media,
      mediaFiles.length,
      imageLayout,
      selectedHashtags,
      selectedFeeling,
      taggedBuddies,
    ],
  );

  const canSubmit = useMemo(() => {
    // If a comparison layout is chosen, it strictly requires 2 or 4 images.
    // Otherwise (single photo or multi-photo gallery posts), any count up to
    // POST_GALLERY_MAX_IMAGES (6) is valid.
    const isComparison =
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_2 ||
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_4;

    const hasValidMediaAlbum =
      isEditMode ||
      (!isComparison
        ? mediaFiles.length <= POST_GALLERY_MAX_IMAGES
        : (imageLayout === POST_IMAGE_LAYOUT.COMPARISON_2 && mediaFiles.length === 2) ||
          (imageLayout === POST_IMAGE_LAYOUT.COMPARISON_4 && mediaFiles.length === 4));

    return (
      hasValidMediaAlbum &&
      (content.trim().length > 0 ||
        media !== null ||
        mediaFiles.length > 0 ||
        selectedHashtags.length > 0 ||
        selectedFeeling !== null ||
        taggedBuddies.length > 0 ||
        protectedReshareContent !== null)
    );
  }, [
    content,
    imageLayout,
    isEditMode,
    media,
    mediaFiles.length,
    selectedHashtags,
    selectedFeeling,
    taggedBuddies,
    protectedReshareContent,
  ]);

  const handleContentChange = useCallback((text: string) => {
    setContent(text);
  }, []);

  const handleAddHashtag = useCallback((tag: string) => {
    const normalized = normalizeHashtagLabel(tag);
    if (!normalized) {
      return;
    }

    setSelectedHashtags(prev => {
      if (prev.includes(normalized)) {
        return prev;
      }
      return [...prev, normalized];
    });
  }, []);

  const handleRemoveHashtag = useCallback((tag: string) => {
    setSelectedHashtags(prev => prev.filter(item => item !== tag));
  }, []);

  const handleSelectFeeling = useCallback((feelingKey: string | null) => {
    setSelectedFeeling(feelingKey);
    if (feelingKey) {
      setActiveSheet(null);
    }
  }, []);

  const handleToggleBuddy = useCallback((buddy: TaggedBuddy) => {
    setTaggedBuddies(prev => {
      const key = buddyKey(buddy);
      if (!key) return prev;
      const exists = prev.some(item => buddyKey(item) === key);
      if (exists) {
        return prev.filter(item => buddyKey(item) !== key);
      }
      return [...prev, buddy];
    });
  }, []);

  const handleRemoveBuddy = useCallback((key: string) => {
    if (!key) return;
    setTaggedBuddies(prev => prev.filter(item => buddyKey(item) !== key));
  }, []);

  const openSheet = useCallback((sheet: CreatePostSheet) => {
    runAfterKeyboardDismiss(() => {
      setActiveSheet(sheet);
    });
  }, []);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
  }, []);

  const handleMediaPicked = useCallback(
    (response: MediaPickResponse) => {
      if (response?.success) {
        const assets =
          response.assets && response.assets.length > 0
            ? response.assets
            : response.asset?.uri
              ? [response.asset]
              : [];

        if (assets.length > 0) {
          const firstAsset = assets[0];
          const mime = (firstAsset.type || '').toLowerCase();
          const explicitVideo = firstAsset.mediaType === POST_MEDIA_TYPE.VIDEO;
          const isVideo =
            explicitVideo ||
            mime.startsWith('video/') ||
            /\.(mp4|mov|m4v|mpeg|mpg|webm)$/i.test(firstAsset.uri) ||
            /\.(mp4|mov|m4v|mpeg|mpg|webm)$/i.test(firstAsset.fileName || '');

          if (isVideo) {
            const nextMedia = {
              ...firstAsset,
              type: mime.startsWith('video/') ? firstAsset.type : 'video/mp4',
              mediaType: POST_MEDIA_TYPE.VIDEO,
            } as MediaFile;
            setMedia(nextMedia);
            setMediaFiles([]);
            setImageLayout(null);
          } else {
            const imageFiles = assets
              .slice(0, POST_GALLERY_MAX_IMAGES)
              .map(a =>
                toImageMediaFile({
                  ...a,
                  mediaType: POST_MEDIA_TYPE.IMAGE,
                } as MediaFile),
              );

            setMedia(imageFiles[0]);
            setMediaFiles(imageFiles);
            setImageLayout(
              imageFiles.length > 1
                ? POST_IMAGE_LAYOUT.GALLERY
                : POST_IMAGE_LAYOUT.SINGLE,
            );
          }
        }
      }
      closeSheet();
    },
    [closeSheet],
  );

  const appendMediaFiles = useCallback(
    (newFiles: MediaFile[]) => {
      if (!newFiles || newFiles.length === 0) return;
      const normalized = newFiles
        .filter(
          f =>
            !!f?.uri &&
            f.mediaType !== POST_MEDIA_TYPE.VIDEO &&
            f.mediaType !== POST_MEDIA_TYPE.GIF,
        )
        .map(toImageMediaFile);

      setMediaFiles(prev => {
        const combined = [...prev, ...normalized].slice(
          0,
          POST_GALLERY_MAX_IMAGES,
        );
        if (combined.length > 0) {
          setMedia(combined[0]);
          setImageLayout(
            combined.length > 1
              ? POST_IMAGE_LAYOUT.GALLERY
              : POST_IMAGE_LAYOUT.SINGLE,
          );
        }
        return combined;
      });
    },
    [],
  );

  const handleGifSelect = useCallback(
    (gifAsset: { uri: string; fileName?: string; type?: string; fileSize?: number }) => {
      setMedia({
        uri: gifAsset.uri,
        mediaType: POST_MEDIA_TYPE.GIF,
        fileName: gifAsset.fileName || 'animation.gif',
        type: gifAsset.type || 'image/gif',
        fileSize: gifAsset.fileSize,
      } as MediaFile);
      setMediaFiles([]);
      setImageLayout(null);
      closeSheet();
    },
    [closeSheet],
  );

  const handleRemoveMedia = useCallback(() => {
    setMedia(null);
    setMediaFiles([]);
    setImageLayout(null);
  }, []);

  const handleRemoveMediaAt = useCallback((index: number) => {
    setMediaFiles(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setMedia(null);
        setImageLayout(null);
        return [];
      }
      setMedia(next[0]);
      setImageLayout(prevLayout => {
        if (next.length === 1) {
          return POST_IMAGE_LAYOUT.SINGLE;
        }
        if (prevLayout === POST_IMAGE_LAYOUT.COMPARISON_2 && next.length === 2) {
          return POST_IMAGE_LAYOUT.COMPARISON_2;
        }
        if (prevLayout === POST_IMAGE_LAYOUT.COMPARISON_4 && next.length === 4) {
          return POST_IMAGE_LAYOUT.COMPARISON_4;
        }
        return POST_IMAGE_LAYOUT.GALLERY;
      });
      return next;
    });
  }, []);

  const setCompareImages = useCallback(
    (layout: PostImageLayout, files: MediaFile[]) => {
      const normalized = files
        .filter(file => !!file?.uri)
        .map(file => toImageMediaFile(file));
      if (normalized.length === 0) {
        if (isEditMode && editingPost) {
          const published = resolvePublishedPostMedia(editingPost);
          setMedia(published.media);
          setMediaFiles(published.mediaFiles);
          setImageLayout(published.imageLayout);
        } else {
          setMedia(null);
          setMediaFiles([]);
          setImageLayout(null);
        }
        setActiveSheet(null);
        return;
      }
      // Keep a stable new array so Create Post preview always re-renders both thumbs.
      const album = normalized.map(file => ({ ...file }));
      setMediaFiles(album);
      setMedia(album[0]);
      setImageLayout(layout);
      setActiveSheet(null);
    },
    [isEditMode, editingPost],
  );

  const loadPostForEdit = useCallback((post: FeedPost) => {
    if (!post) {
      return;
    }

    const resolvedId = String(post.id ?? '').trim();
    if (!/^\d+$/.test(resolvedId) || /^0+$/.test(resolvedId)) {
      console.warn(
        '[CreatePost] Cannot edit post — missing or invalid numeric post id:',
        post.id,
      );
    }

    setEditingPostId(resolvedId);
    _setEditingPost(post);

    const postHashtags = normalizeHashtagList(
      post.hashtags || (post as any).hashTags,
    );
    const rawBody =
      post.content ??
      post.text ??
      (post as any).postDescription ??
      (post as any).bodyText ??
      '';
    const { caption, separateHashtags } = splitSeparateHashtags(
      rawBody,
      postHashtags,
    );
    const editableReshare = splitEditableReshareContent(caption);

    // Attribution, original text, and source metadata remain protected. Only
    // the member's thoughts are editable and the source is reattached on save.
    setProtectedReshareContent(editableReshare?.protectedContent ?? null);
    setContent(
      collapseHashtagPrefixes(editableReshare?.thoughts ?? caption),
    );
    const resolvedPrivacy = mapVisibilityIntToPrivacy(
      post.privacy ??
        (post as unknown as { postVisibility?: number | string }).postVisibility ??
        (post as unknown as { privacyId?: number | string }).privacyId,
    );
    setPrivacy(resolvedPrivacy);
    // Hashtags selected separately belong to this reshare post, not to the
    // protected original snapshot. Keep them editable and preserve them when
    // an unrelated thought change is saved.
    setSelectedHashtags(separateHashtags);
    setSelectedFeeling(post.feeling ?? null);
    const initialTaggedBuddies = post.taggedBuddies ?? [];
    setTaggedBuddies(initialTaggedBuddies);
    originalTaggedBuddiesRef.current = initialTaggedBuddies;

    // Read-only in the editor: the same resolver decides what a save may send,
    // so the preview and the update payload cannot drift apart.
    const published = resolvePublishedPostMedia(post);
    setMedia(published.media);
    setMediaFiles(published.mediaFiles);
    setImageLayout(published.imageLayout);
  }, []);

  const handleSelectPrivacy = useCallback(
    (value: PostPrivacy) => {
      setPrivacy(value);
      closeSheet();
    },
    [closeSheet],
  );

  const resetForm = useCallback(() => {
    const initial = resetFormState();
    setContent(initial.content);
    setPrivacy(initial.privacy);
    setSelectedHashtags(initial.selectedHashtags);
    setSelectedFeeling(initial.selectedFeeling);
    setTaggedBuddies(initial.taggedBuddies);
    setMedia(initial.media);
    setMediaFiles(initial.mediaFiles);
    setImageLayout(initial.imageLayout);
    setCreatedPostId(null);
    setEditingPostId(null);
    _setEditingPost(null);
    setProtectedReshareContent(null);
    originalTaggedBuddiesRef.current = [];
    setActiveSheet(null);
    setShowDiscardModal(false);
    setShowSuccessModal(false);
    setLoading(false);
  }, []);

  const handleKeepEditing = useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleDiscardPost = useCallback(
    (onNavigateAway?: () => void) => {
      setShowDiscardModal(false);
      resetForm();
      onNavigateAway?.();
    },
    [resetForm],
  );

  const validatePost = useCallback(() => {
    const trimmed = convertPostContentToPlainText(content);
    if (
      !trimmed &&
      !media &&
      mediaFiles.length === 0 &&
      !selectedFeeling &&
      !selectedHashtags.length &&
      !taggedBuddies.length &&
      protectedReshareContent === null
    ) {
      ToastService.show({
        type: 'error',
        message: t('home.createPost.contentRequired'),
      });
      return false;
    }
    // Comparison layouts need exactly 2 or 4 images. Gallery allows any
    // count ≥ 1. The count-vs-layout mismatch is caught by resolveLayoutForAlbum.
    if (
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_2 &&
      mediaFiles.length !== 2
    ) {
      ToastService.show({
        type: 'error',
        message: t('home.createPost.media.compareIncomplete'),
      });
      return false;
    }
    if (
      imageLayout === POST_IMAGE_LAYOUT.COMPARISON_4 &&
      mediaFiles.length !== 4
    ) {
      ToastService.show({
        type: 'error',
        message: t('home.createPost.media.compareIncomplete'),
      });
      return false;
    }
    // Other media rules only apply to new posts. An edit without compare layout
    // carries the published media untouched.
    if (editingPostId) {
      return true;
    }
    // Any album of 3+ photos travels as a gallery even when no layout was
    // picked, so the cap is checked against the resolved layout.
    if (
      resolveLayoutForAlbum(mediaFiles.length, imageLayout) === POST_IMAGE_LAYOUT.GALLERY &&
      mediaFiles.length > POST_GALLERY_MAX_IMAGES
    ) {
      ToastService.show({
        type: 'error',
        message: t('home.createPost.media.galleryMaxImages', {
          max: POST_GALLERY_MAX_IMAGES,
        }),
      });
      return false;
    }
    return true;
  }, [
    content,
    editingPostId,
    imageLayout,
    media,
    mediaFiles.length,
    selectedFeeling,
    selectedHashtags.length,
    taggedBuddies.length,
    protectedReshareContent,
    t,
  ]);

  const handleSubmit = useCallback(async (
    extraPayload: any = {},
  ): Promise<CreatePostSubmitResult> => {
    if (loading) {
      return FAILED_SUBMIT;
    }
    if (!validatePost()) {
      return FAILED_SUBMIT;
    }

    setLoading(true);
    setSubmittedHeldForReview(false);
    try {
      const isGroupPost = !!extraPayload.groupId;
      const honorComposerMedia = extraPayload?.honorComposerMedia === true;
      const postExtras = { ...(extraPayload ?? {}) };
      delete postExtras.honorComposerMedia;
      // When the caller asks to honor the composer, removing the image, GIF, or
      // video leaves no media, so the update is saved as text.
      const submitMedia = resolveSubmittedPostMedia({
        isEditing: isEditMode,
        publishedPost: editingPost,
        composerMedia: { media, mediaFiles, imageLayout },
        honorComposerMedia,
      });
      const cleanContent = convertPostContentToPlainText(content);
      const payload = {
        content:
          protectedReshareContent === null
            ? cleanContent
            : rebuildEditedReshareContent(
                cleanContent,
                protectedReshareContent,
              ),
        privacy,
        hashtags: selectedHashtags,
        feeling: selectedFeeling,
        // Group posts never include Tag Buddies.
        taggedBuddies: isGroupPost ? [] : taggedBuddies.map(buddy => buddy.id),
        taggedBuddyDetails: isGroupPost ? [] : taggedBuddies,
        media: submitMedia.media,
        mediaFiles:
          submitMedia.mediaFiles.length > 0 ? submitMedia.mediaFiles : undefined,
        imageLayout: resolveLayoutForAlbum(
          submitMedia.mediaFiles.length,
          submitMedia.imageLayout,
        ),
        ...postExtras,
        ...(editingPostId &&
        !isGroupPost &&
        taggedBuddyListsEqual(taggedBuddies, originalTaggedBuddiesRef.current)
          ? { omitBuddiesTags: true }
          : {}),
      };

      if (editingPostId) {
        if (!/^\d+$/.test(String(editingPostId).trim())) {
          console.error(
            '[createPostContext] Refusing update — invalid editingPostId:',
            editingPostId,
          );
          ToastService.show({
            type: 'error',
            message: t('home.createPost.updateErrorMessage'),
          });
          return FAILED_SUBMIT;
        }
        const response = await updatePost(editingPostId, payload, profile, t);
        if (response?.success) {
          const heldForReview = isHeldForReview(
            response.data?.moderationStatus,
          );
          setSubmittedHeldForReview(heldForReview);
          if (isGroupPost && response.data?.post) {
            const optimistic = response.data.post;
            updateGroupPost(editingPostId, {
              text: optimistic.text,
              content: optimistic.content,
              feeling: optimistic.feeling,
              hashtags: optimistic.hashtags,
              taggedBuddies: [],
              privacy: optimistic.privacy,
              image: optimistic.image,
              images: optimistic.images,
              imageLayout: optimistic.imageLayout,
              video: optimistic.video,
              moderationStatus: optimistic.moderationStatus,
            });
          } else if (!isGroupPost && !response.data?.post) {
            // `updatePost` already writes the saved post into every feed cache,
            // so a refetch is only the fallback for an API answer that carried no
            // post. Refetching otherwise would drop the feed back to page one and
            // throw away the position the editor was opened from.
            refreshFeed();
          }
          resetForm();
          return { success: true, heldForReview };
        }
        throw new Error('update_post_failed');
      }

      const response = await feedService.createPost(payload, profile, t);

      const createdPost = response.data?.post;
      // Without a server id the optimistic card cannot be commented on, liked or
      // deleted, so pull the persisted copy instead of prepending a dead one.
      const hasServerId = isPersistedPostId(createdPost?.id);

      if (response.success && createdPost) {
        if (isGroupPost) {
          if (hasServerId) {
            prependGroupPosts([
              {
                ...createdPost,
                taggedBuddies: [],
                groupId: String(payload.groupId),
                groupName: payload.groupName,
                isGroupPost: true,
              },
            ]);
          } else {
            await fetchGroupPosts(String(payload.groupId), { force: true });
          }
        } else if (hasServerId) {
          syncNewPosts([createdPost]);
        } else {
          await refreshFeed();
        }
      }

      if (response.success) {
        const heldForReview = isHeldForReview(response.data?.moderationStatus);
        setCreatedPostId(hasServerId ? String(createdPost!.id) : null);
        setSubmittedHeldForReview(heldForReview);
        setShowSuccessModal(true);
        const primaryMedia =
          submitMedia.mediaFiles[0] ?? submitMedia.media ?? null;
        const postAnalytics = {
          post_type: primaryMedia?.mediaType ?? (primaryMedia ? 'image' : 'text'),
          source: isGroupPost ? 'group' : 'feed',
        };
        analytics.trackEvent('post_created', postAnalytics);
        analytics.trackOnce(
          STORAGE_KEYS.ANALYTICS_FIRST_POST_CREATED,
          ANALYTICS_EVENTS.ACTIVATION_FIRST_POST_CREATED,
          postAnalytics,
        );
        // Stay on Create Post with the success modal — do not navigate or reset yet.
        return { success: true, heldForReview };
      }
      // The API's own reason (rejected media, size limit, ...) is far more
      // actionable than a generic "could not create post".
      if (response.message) {
        ToastService.show({ type: 'error', message: response.message });
        return FAILED_SUBMIT;
      }
      throw new Error('create_post_failed');
    } catch (err) {
      console.error('[createPostContext] Error saving post:', err);
      const message =
        err instanceof BuddiesTagsUnresolvedError
          ? t('home.createPost.taggedBuddiesUnresolved')
          : editingPostId
            ? t('home.createPost.updateErrorMessage')
            : t('home.createPost.errorMessage');
      ToastService.show({
        type: 'error',
        message,
      });
      return FAILED_SUBMIT;
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    content,
    privacy,
    selectedHashtags,
    selectedFeeling,
    taggedBuddies,
    media,
    mediaFiles,
    imageLayout,
    profile,
    validatePost,
    updatePost,
    editingPostId,
    editingPost,
    protectedReshareContent,
    isEditMode,
    prependGroupPosts,
    updateGroupPost,
    fetchGroupPosts,
    syncNewPosts,
    refreshFeed,
    resetForm,
    t,
  ]);

  const handleBackToFeed = useCallback(
    (onNavigateAway?: () => void) => {
      setShowSuccessModal(false);
      resetForm();
      onNavigateAway?.();
      // Feed already holds the new post (prepended or refreshed) — no GetFeeds.
    },
    [resetForm],
  );

  const handleViewPost = useCallback(
    (onNavigateAway?: () => void) => {
      setShowSuccessModal(false);
      resetForm();
      onNavigateAway?.();
    },
    [resetForm],
  );

  const handleCloseSuccessModal = useCallback(
    (onNavigateAway?: () => void) => {
      setShowSuccessModal(false);
      resetForm();
      onNavigateAway?.();
    },
    [resetForm],
  );

  const promptDiscard = useCallback(() => {
    setShowDiscardModal(true);
  }, []);

  const value = useMemo<CreatePostContextValue>(
    () => ({
      profile,
      content,
      privacy,
      selectedHashtags,
      selectedFeeling,
      taggedBuddies,
      media,
      mediaFiles,
      imageLayout,
      loading,
      activeSheet,
      showDiscardModal,
      showSuccessModal,
      createdPostId,
      submittedHeldForReview,
      editingPostId,
      editingPost,
      isEditMode,
      hasUnsavedChanges,
      canSubmit,
      handleContentChange,
      handleAddHashtag,
      handleRemoveHashtag,
      handleSelectFeeling,
      handleToggleBuddy,
      handleRemoveBuddy,
      openSheet,
      closeSheet,
      handleMediaPicked,
      appendMediaFiles,
      handleGifSelect,
      handleRemoveMedia,
      handleRemoveMediaAt,
      setCompareImages,
      loadPostForEdit,
      handleSelectPrivacy,
      promptDiscard,
      handleKeepEditing,
      handleDiscardPost,
      handleSubmit,
      handleBackToFeed,
      handleViewPost,
      handleCloseSuccessModal,
      resetForm,
    }),
    [
      profile,
      content,
      privacy,
      selectedHashtags,
      selectedFeeling,
      taggedBuddies,
      media,
      mediaFiles,
      imageLayout,
      loading,
      activeSheet,
      showDiscardModal,
      showSuccessModal,
      createdPostId,
      submittedHeldForReview,
      editingPostId,
      editingPost,
      isEditMode,
      hasUnsavedChanges,
      canSubmit,
      handleContentChange,
      handleAddHashtag,
      handleRemoveHashtag,
      handleSelectFeeling,
      handleToggleBuddy,
      handleRemoveBuddy,
      openSheet,
      closeSheet,
      handleMediaPicked,
      appendMediaFiles,
      handleGifSelect,
      handleRemoveMedia,
      handleRemoveMediaAt,
      setCompareImages,
      loadPostForEdit,
      handleSelectPrivacy,
      promptDiscard,
      handleKeepEditing,
      handleDiscardPost,
      handleSubmit,
      handleBackToFeed,
      handleViewPost,
      handleCloseSuccessModal,
      resetForm,
    ],
  );

  return (
    <createPostContext.Provider value={value}>
      {children}
    </createPostContext.Provider>
  );
};

export const useCreatePostData = (): CreatePostContextValue => {
  const createPost = useContext(createPostContext);
  if (!createPost) {
    throw new Error('useCreatePostData must be used within CreatePostProvider');
  }
  return createPost;
};
